import type { ClassRoom, VacationSlot } from '@/lib/types';
import { formatTime } from '@/lib/constants';
import type { PrintDay } from '@/pages/timetable/utils';

export const UNASSIGNED_TEACHER_ID = '__unassigned__';

// 표시에 필요한 값들을 모두 계산해 둔 형태예요.
// name/subject/teacher_id 가 비어 있으면 연결된 반(classes) 값을 사용해요.
export type ResolvedSlot = {
  id: string;
  name: string;
  subject: string | null;
  teacherId: string | null;
  teacherName: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  students: string[];
  raw: VacationSlot;
};

export function resolveSlot(
  slot: VacationSlot,
  classMap: Map<string, ClassRoom>,
  teacherMap: Map<string, string>,
  classRoster: Record<string, string[]>,
  slotRoster: Record<string, string[]>
): ResolvedSlot {
  const cls = slot.class_id ? classMap.get(slot.class_id) : undefined;
  const name = (slot.name && slot.name.trim()) || cls?.name || '특강';
  const subject = (slot.subject && slot.subject.trim()) || cls?.subject || null;
  const teacherId = slot.teacher_id || cls?.teacher_id || null;
  const teacherName = teacherId ? teacherMap.get(teacherId) || '이름 없음' : '담당 미배정';
  // 항목에 별도 명단이 있으면 그 명단을, 없으면 연결된 반 명단을 사용해요.
  const students = slot.students_customized
    ? slotRoster[slot.id] || []
    : slot.class_id
      ? classRoster[slot.class_id] || []
      : [];
  return {
    id: slot.id,
    name,
    subject,
    teacherId,
    teacherName,
    dayOfWeek: slot.day_of_week,
    startTime: slot.start_time || '',
    endTime: slot.end_time || '',
    students,
    raw: slot,
  };
}

export type VacationGroup = {
  id: string;
  name: string;
  isUnassigned: boolean;
  items: ResolvedSlot[];
};

// 담당 선생님을 기준으로 방학특강 시간표를 묶어요. 담당이 없으면 '담당 미배정'으로 모아요.
export function buildVacationGroups(
  slots: VacationSlot[],
  classMap: Map<string, ClassRoom>,
  teacherMap: Map<string, string>,
  classRoster: Record<string, string[]>,
  slotRoster: Record<string, string[]>
): VacationGroup[] {
  const groups = new Map<string, VacationGroup>();
  slots.forEach((slot) => {
    const resolved = resolveSlot(slot, classMap, teacherMap, classRoster, slotRoster);
    const key = resolved.teacherId || UNASSIGNED_TEACHER_ID;
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        name: key === UNASSIGNED_TEACHER_ID ? '담당 미배정' : resolved.teacherName,
        isUnassigned: key === UNASSIGNED_TEACHER_ID,
        items: [],
      });
    }
    groups.get(key)!.items.push(resolved);
  });

  groups.forEach((g) => {
    g.items.sort(
      (a, b) =>
        a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime)
    );
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.isUnassigned) return 1;
    if (b.isUnassigned) return -1;
    return a.name.localeCompare(b.name, 'ko');
  });
}

// 인쇄용으로 요일(월~일) 순서대로 항목을 정리해요.
export function buildVacationPrintDays(items: ResolvedSlot[], showTeacher: boolean): PrintDay[] {
  const order = [1, 2, 3, 4, 5, 6, 0];
  return order.map((day) => ({
    day,
    entries: items
      .filter((i) => i.dayOfWeek === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
      .map((i) => ({
        className: i.name,
        subject: i.subject,
        time: `${formatTime(i.startTime)}~${formatTime(i.endTime)}`,
        teacher: showTeacher ? i.teacherName : undefined,
        students: i.students,
      })),
  }));
}